import React, { useCallback, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import PropTypes from 'prop-types';

import isEmpty from 'lodash-es/isEmpty';

import { getInitialQueryString } from '@ecp/common/src/utils/good/goodSearchUtil';
import useSearchHistory from '@ecp/common/src/hooks/useSearchHistory';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import SearchInput from '@ecp/common/src/components/input/mo/SearchInput';
import { SITE_SKYPASS } from '@ecp/common/src/const/site/siteNumberConst.js';

import { searchKeywordHistoryListState, searchKeywordState } from '@mo-recoil/search/atom';
import { userInfoState } from '@mo-recoil/common/user/atom';
import useSiteInfo from '@mo-hooks/common/useSiteInfo';
import { useAlertDialog } from '@mo-hooks/common/useModal';

const GnbSearchInput = ({ isSearchResultPage, width }) => {
  const [searchKeyword, setSearchKeyword] = useRecoilState(searchKeywordState);
  const setHistoryList = useSetRecoilState(searchKeywordHistoryListState);
  const userInfo = useRecoilValue(userInfoState);

  const { addSearchKeyword } = useSearchHistory(userInfo?.userId);
  const { showAlertDialog } = useAlertDialog();
  const navigate = useNavigate();
  const { siteNumber } = useSiteInfo();

  const searchRef = useRef(null);

  const searchInputPlaceholder = useMemo(() => {
    const isSkypass = siteNumber === SITE_SKYPASS;

    if (isSkypass) {
      return '상품 검색어를 입력해 주세요';
    } else {
      return '검색어를 입력해주세요';
    }
  }, [siteNumber]);

  const handleChange = useCallback(
    (e) => {
      if (isSearchResultPage) {
        navigate('/search');
      }

      setSearchKeyword(e?.target?.value ?? '');
    },
    [isSearchResultPage, navigate, setSearchKeyword]
  );

  useEffect(() => {
    if (!isSearchResultPage && !searchKeyword) {
      searchRef.current?.focus?.();
    }
  }, [isSearchResultPage, searchKeyword]);

  useEffect(() => {
    if (isSearchResultPage && searchKeyword) {
      searchRef.current?.blur?.();
    }
  }, [isSearchResultPage, searchKeyword]);

  const handleSearch = useCallback(() => {
    const searchTerm = searchKeyword?.trim?.();
    if (isEmpty(searchTerm)) {
      showAlertDialog('검색어를 입력해 주세요.');
      return;
    }

    const updatedList = addSearchKeyword(searchTerm);
    setHistoryList(updatedList);

    searchRef.current?.blur?.();

    navigate(
      { pathname: '/search/result', search: getInitialQueryString({ searchTerm: searchTerm, countPerPage: 20 }) },
      { replace: true }
    );
  }, [addSearchKeyword, navigate, searchKeyword, setHistoryList, showAlertDialog]);

  const handleClick = useCallback(() => {
    if (isSearchResultPage) {
      navigate('/search');
    }
  }, [isSearchResultPage, navigate]);

  return (
    <div style={{ width }} data-ds-label2={'g_search_bar'}>
      <SearchInput
        clearable
        autoFocus
        type={'main'}
        placeholder={searchInputPlaceholder}
        ref={searchRef}
        value={searchKeyword}
        onChange={handleChange}
        onEnter={handleSearch}
        onSearchClick={handleSearch}
        onClick={handleClick}
      />
    </div>
  );
};

GnbSearchInput.propTypes = {
  isSearchResultPage: PropTypes.bool,
  width: PropTypes.string,
};

const GnbSubTopSearch = ({ width }) => {
  const { pathname } = useLocation();
  const isSearchResultPage = useMemo(() => pathname === '/search/result', [pathname]);

  return (
    <>
      <Spacing right="14px" />
      <GnbSearchInput isSearchResultPage={isSearchResultPage} width={width} />
    </>
  );
};

GnbSubTopSearch.propTypes = {
  width: PropTypes.string,
};

export default GnbSubTopSearch;
